<script lang="ts" setup>
import { computed, ref } from "vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import FormRadioGroup from "@/components/molecules/form/FormRadioGroup.vue";
import Header from "@/components/organisms/common/Header.vue";
import { accountService } from "@/app/services/AccountService";
import InfoCard from "@/components/atoms/other/InfoCard.vue";
import { useLoader } from "@/utils/loader";
import { useErrorModal, useModal } from "@/utils/modal";
import { useCustomRouter } from "@/utils/router";
import { onMounted } from "vue";

const loader = useLoader();
const errorModal = useErrorModal();
const modal = useModal();
const customRouter = useCustomRouter();

const options = [
  { text: "はい", value: true },
  { text: "いいえ", value: false }
];

const subscribeNewsletter = ref(false);
const isReceivedIndividualOrderByEmailForWorker = ref(false);
const isReceivedLimitedOrderByEmailForWorker = ref(false);

const formValues = computed(() => ({
  subscribeNewsletter: subscribeNewsletter.value,
  isReceivedIndividualOrderByEmailForWorker:
    isReceivedIndividualOrderByEmailForWorker.value,
  isReceivedLimitedOrderByEmailForWorker:
    isReceivedLimitedOrderByEmailForWorker.value
}));

onMounted(async function() {
  loader.show();
  try {
    // authStoreのユーザは古い可能性があるので最新のユーザ情報を取得
    const user = await accountService.fetchUserData();
    subscribeNewsletter.value = user.subscribeNewsletter;
    isReceivedIndividualOrderByEmailForWorker.value =
      user.isReceivedIndividualOrderByEmailForWorker;
    isReceivedLimitedOrderByEmailForWorker.value =
      user.isReceivedLimitedOrderByEmailForWorker;
  } catch (err) {
    await errorModal.showErrorModal(err as Error);
  } finally {
    loader.hide();
  }
});

async function onSubmit() {
  loader.show();
  try {
    await accountService.patchUser({
      subscribeNewsletter: subscribeNewsletter.value,
      isReceivedIndividualOrderByEmailForWorker:
        isReceivedIndividualOrderByEmailForWorker.value,
      isReceivedLimitedOrderByEmailForWorker:
        isReceivedLimitedOrderByEmailForWorker.value
    });
    await modal.msgBoxOk("メール受信設定を変更しました", {
      okVariant: "success",
      centered: true
    });
    await customRouter.pushIgnoreError({ name: "Mypage" });
  } catch (err) {
    await errorModal.showErrorModal(err as Error);
  } finally {
    loader.hide();
  }
}
</script>

<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      メール受信設定
    </Header>
    <CustomValidationObserver
      tag="div"
      class="my-4 mx-3"
      v-slot="{ handleSubmit }"
      :initialValues="formValues"
    >
      <InfoCard body-class="p-2">
        <p class="text-size-14">
          お仕事情報やキャンペーンなどのメールの受信設定を変更できます
        </p>
      </InfoCard>
      <BForm>
        <ValidationProvider
          v-slot="validationState"
          name="指名オファー"
          rules="required_allow_false"
          vid="isReceivedIndividualOrderByEmailForWorker"
          class="mt-3"
        >
          <FormRadioGroup
            label="指名オファー"
            name="指名オファー"
            v-model="isReceivedIndividualOrderByEmailForWorker"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="validationState"
          name="限定公開のお仕事"
          rules="required_allow_false"
          vid="isReceivedLimitedOrderByEmailForWorker"
          class="mt-3"
        >
          <FormRadioGroup
            label="限定公開のお仕事"
            name="限定公開のお仕事"
            v-model="isReceivedLimitedOrderByEmailForWorker"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="validationState"
          name="おすすめのお仕事情報やキャンペーンのお知らせ"
          rules="required_allow_false"
          vid="subscribeNewsletter"
          class="mt-3"
        >
          <FormRadioGroup
            label="おすすめのお仕事情報やキャンペーンのお知らせ"
            name="おすすめのお仕事情報やキャンペーンのお知らせ"
            v-model="subscribeNewsletter"
            :options="options"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <AppButton block class="mt-4" @click="handleSubmit(onSubmit)">
          更新する
        </AppButton>
      </BForm>
    </CustomValidationObserver>
  </div>
</template>
